import type { CompanyFacebookNotification } from 'types/company/company';

export type FacebookHealthArea = 'business' | 'payment' | 'templates';

export type FacebookHealthCheckResult = {
  ok: boolean;
  detail?: string;
};

function textFor(row: CompanyFacebookNotification): string {
  const v = row.value;
  return [v.alert_type, v.alert_description, v.entity_type, v.alert_severity, v.alert_status]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function isResolved(row: CompanyFacebookNotification): boolean {
  const s = (row.value.alert_status || '').toLowerCase();
  if (!s) return false;
  return /resolved|closed|dismissed|cleared|^ok$|completed/.test(s);
}

function matchesPayment(text: string): boolean {
  return (
    /\b(payment|billing|invoice|charge|wallet|pay_method|payment_method|credit|debit|payment_method_update|billing|past_due|overdue)\b/.test(
      text,
    ) || /תשלום|חיוב|אמצעי תשלום|חשבון חיוב/i.test(text)
  );
}

function matchesTemplates(text: string): boolean {
  return (
    /\b(template|templates|quality|throughput|tier|messaging_limit|rate_limit|message_template|template_quality)\b/.test(
      text,
    ) || /תבנית|טמפלט|איכות תבניות|תבניות הודעה/i.test(text)
  );
}

function matchesBusiness(text: string): boolean {
  if (matchesPayment(text) || matchesTemplates(text)) return false;
  return (
    /\b(business|waba|verification|portfolio|merchant|commerce|business_verification|business_info|display_name|account_update|whatsapp_business|phone_number)\b/.test(
      text,
    ) || /עסק|אימות|חשבון עסקי|waba|פורטפוליו/i.test(text)
  );
}

function findProblem(
  notifications: CompanyFacebookNotification[],
  matcher: (t: string) => boolean,
): FacebookHealthCheckResult {
  const active = notifications.filter((n) => !isResolved(n));
  for (const row of active) {
    const t = textFor(row);
    if (matcher(t)) {
      return { ok: false, detail: row.value.alert_description || row.value.alert_type };
    }
  }
  return { ok: true };
}

function registrationSuggestsBusinessIssue(registrationStatus: string): boolean {
  const s = registrationStatus.toLowerCase();
  return /fail|reject|suspend|block|invalid|disabled|restricted|revoked/i.test(s);
}

export function classifyFacebookHealth(
  notifications: CompanyFacebookNotification[],
  companyRegistrationStatus?: string | null,
): Record<FacebookHealthArea, FacebookHealthCheckResult> {
  const businessFromAlerts = findProblem(notifications, matchesBusiness);
  const payment = findProblem(notifications, matchesPayment);
  const templates = findProblem(notifications, matchesTemplates);

  let business = businessFromAlerts;
  if (
    business.ok &&
    companyRegistrationStatus &&
    registrationSuggestsBusinessIssue(companyRegistrationStatus)
  ) {
    business = { ok: false, detail: companyRegistrationStatus };
  }

  return { business, payment, templates };
}
